import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  AttachMoney as ReimburseIcon,
  Send as SubmitIcon,
  Receipt as ReceiptIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  AttachFile as AttachmentIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Button, Card } from '../../../design-system/components/core';
import MagicLoading from '../../../components/ui/MagicLoading';
import TabPanel from '../../../components/ui/TabPanel';
import EntityStatusBadge from '../../../components/core/EntityStatusBadge';
import Breadcrumbs from '../../../components/core/Breadcrumbs';
import useExpenseStore from '../../../stores/expenseStore';
import useAuthStore from '../../../stores/authStore';
import useConfirmationDialog from '../../../hooks/useConfirmationDialog';
import useUIStore from '../../../stores/uiStore';
import SecureReceiptViewer from '../../../components/ui/SecureReceiptViewer';

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    currentExpense,
    isLoading,
    fetchExpenseById,
    submitForApproval,
    approveExpense,
    rejectExpense,
    deleteExpense,
    uploadReceipt,
    deleteReceipt
  } = useExpenseStore();
  
  const { user } = useAuthStore();
  const { addNotification } = useUIStore();
  const [ConfirmDialog, confirm] = useConfirmationDialog();
  
  const [tabValue, setTabValue] = useState(0);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionComment, setActionComment] = useState('');
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchExpenseById(id);
  }, [id, fetchExpenseById]);

  const breadcrumbs = [
    { label: 'Finances', path: '/finances' },
    { label: 'Expenses', path: '/finances/expenses' },
    { label: currentExpense?.expenseNumber || 'Loading...' }
  ];

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Expense',
      content: `Are you sure you want to delete expense ${currentExpense.expenseNumber}?`,
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteExpense(currentExpense._id);
        addNotification({
          message: 'Expense deleted successfully',
          type: 'success'
        });
        navigate('/finances/expenses');
      } catch (error) {
        addNotification({
          message: 'Failed to delete expense',
          type: 'error'
        });
      }
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      // TODO: Add approver selection logic
      const approverId = user.managerId;
      await submitForApproval(currentExpense._id, approverId);
      addNotification({
        message: 'Expense submitted for approval',
        type: 'success'
      });
    } catch (error) {
      addNotification({
        message: 'Failed to submit expense',
        type: 'error'
      });
    }
  };

  const handleActionDialog = (type) => {
    setActionType(type);
    setActionComment('');
    setActionDialogOpen(true);
  };

  const handleActionSubmit = async () => {
    try {
      if (actionType === 'approve') {
        await approveExpense(currentExpense._id, actionComment);
        addNotification({
          message: 'Expense approved',
          type: 'success'
        });
      } else if (actionType === 'reject') {
        await rejectExpense(currentExpense._id, actionComment);
        addNotification({
          message: 'Expense rejected',
          type: 'success'
        });
      }
      setActionDialogOpen(false);
    } catch (error) {
      addNotification({
        message: `Failed to ${actionType} expense`,
        type: 'error'
      });
    }
  };

  const handleReceiptUpload = async () => {
    if (!selectedFile) {
      addNotification({
        message: 'Please select a file to upload',
        type: 'error'
      });
      return;
    }

    try {
      await uploadReceipt(currentExpense._id, selectedFile);
      addNotification({
        message: 'Receipt uploaded successfully',
        type: 'success'
      });
      setReceiptDialogOpen(false);
      setSelectedFile(null);
    } catch (error) {
      addNotification({
        message: 'Failed to upload receipt',
        type: 'error'
      });
    }
  };

  const handleReceiptDelete = async (receiptId) => {
    const confirmed = await confirm({
      title: 'Delete Receipt',
      content: 'Are you sure you want to delete this receipt?',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteReceipt(currentExpense._id, receiptId);
        addNotification({
          message: 'Receipt deleted successfully',
          type: 'success'
        });
      } catch (error) {
        addNotification({
          message: 'Failed to delete receipt',
          type: 'error'
        });
      }
    }
  };

  if (isLoading || !currentExpense) {
    return <MagicLoading />;
  }

  const canEdit = currentExpense.status === 'draft' && currentExpense.owner._id === user._id;
  const canApprove = currentExpense.status === 'pending_approval' && 
                    currentExpense.currentApprover?._id === user._id;
  const canReimburse = ['approved', 'pending_reimbursement'].includes(currentExpense.status) &&
                       user.permissions?.includes('expense.reimburse');

  return (
    <Box sx={{ position: 'relative' }}>
      <Breadcrumbs items={breadcrumbs} />
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/finances/expenses')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" gutterBottom>
              {currentExpense.expenseNumber}
            </Typography>
            <EntityStatusBadge entity="expense" status={currentExpense.status} />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <>
              <Button
                startIcon={<EditIcon />}
                onClick={() => navigate(`/finances/expenses/${id}/edit`)}
              >
                Edit
              </Button>
              <Button
                startIcon={<SubmitIcon />}
                onClick={handleSubmitForApproval}
                variant="contained"
              >
                Submit for Approval
              </Button>
              <Button
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                color="error"
              >
                Delete
              </Button>
            </>
          )}
          
          {canApprove && (
            <>
              <Button
                startIcon={<ApproveIcon />}
                onClick={() => handleActionDialog('approve')}
                color="success"
                variant="contained"
              >
                Approve
              </Button>
              <Button
                startIcon={<RejectIcon />}
                onClick={() => handleActionDialog('reject')}
                color="error"
              >
                Reject
              </Button>
            </>
          )}
          
          {canReimburse && (
            <Button
              startIcon={<ReimburseIcon />}
              onClick={() => navigate(`/finances/expenses/${id}/reimburse`)}
              variant="contained"
            >
              Mark as Reimbursed
            </Button>
          )}
        </Box>
      </Box>

      {/* Overdue Alert */}
      {currentExpense.status === 'pending_reimbursement' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This expense is pending reimbursement
        </Alert>
      )}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
          <Tab label="Overview" />
          <Tab label="Receipts" />
          <Tab label="History" />
        </Tabs>
      </Card>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card level={1}>
              <Typography variant="h6" gutterBottom>
                Expense Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Date
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.date ? format(new Date(currentExpense.date), 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    ${currentExpense.amount ? currentExpense.amount.toFixed(2) : '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Tax Rate
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.taxRate ? `${currentExpense.taxRate}%` : '0%'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Tax Amount
                  </Typography>
                  <Typography variant="body1">
                    ${currentExpense.taxAmount ? currentExpense.taxAmount.toFixed(2) : '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    ${currentExpense.totalAmount ? currentExpense.totalAmount.toFixed(2) : currentExpense.amount ? currentExpense.amount.toFixed(2) : '0.00'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.category ? currentExpense.category.replace('_', ' ').toUpperCase() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.paymentMethod ? currentExpense.paymentMethod.replace('_', ' ').toUpperCase() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">
                    Vendor
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.vendor || 'No vendor specified'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">
                    Billable
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.billable ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {currentExpense.description}
                  </Typography>
                </Grid>
                {currentExpense.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {currentExpense.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Card>

            {/* Associated Entities */}
            {(currentExpense.clientId || currentExpense.projectId || currentExpense.campaignId) && (
              <Card level={1} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Associated With
                </Typography>
                <Grid container spacing={2}>
                  {currentExpense.clientId && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary">
                        Client
                      </Typography>
                      <Typography variant="body1">
                        <Link 
                          component="button" 
                          onClick={() => currentExpense.clientId?._id && navigate(`/clients/${currentExpense.clientId._id}`)}
                        >
                          {currentExpense.clientId?.name || 'N/A'}
                        </Link>
                      </Typography>
                    </Grid>
                  )}
                  {currentExpense.projectId && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary">
                        Project
                      </Typography>
                      <Typography variant="body1">
                        <Link 
                          component="button" 
                          onClick={() => currentExpense.projectId?._id && navigate(`/projects/${currentExpense.projectId._id}`)}
                        >
                          {currentExpense.projectId?.name || 'N/A'}
                        </Link>
                      </Typography>
                    </Grid>
                  )}
                  {currentExpense.campaignId && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary">
                        Campaign
                      </Typography>
                      <Typography variant="body1">
                        <Link 
                          component="button" 
                          onClick={() => currentExpense.campaignId?._id && navigate(`/campaigns/${currentExpense.campaignId._id}`)}
                        >
                          {currentExpense.campaignId?.name || 'N/A'}
                        </Link>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Owner Info */}
            <Card level={2}>
              <Typography variant="h6" gutterBottom>
                Expense Owner
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar>
                  {currentExpense.owner?.firstName?.[0] || '?'}
                  {currentExpense.owner?.lastName?.[0] || ''}
                </Avatar>
                <Box>
                  <Typography variant="body1">
                    {currentExpense.owner?.firstName || 'Unknown'} {currentExpense.owner?.lastName || 'User'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {currentExpense.owner?.email || 'No email'}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Approval Info */}
            {currentExpense.currentApprover && (
              <Card level={2} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Pending Approval From
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar>
                    {currentExpense.currentApprover?.firstName?.[0] || '?'}
                    {currentExpense.currentApprover?.lastName?.[0] || ''}
                  </Avatar>
                  <Box>
                    <Typography variant="body1">
                      {currentExpense.currentApprover?.firstName || 'Unknown'} {currentExpense.currentApprover?.lastName || 'Approver'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {currentExpense.currentApprover?.email || 'No email'}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Reimbursement Info */}
            {currentExpense.reimbursementStatus === 'completed' && (
              <Card level={2} sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Reimbursement
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Date
                  </Typography>
                  <Typography variant="body2">
                    {currentExpense.reimbursementDate ? format(new Date(currentExpense.reimbursementDate), 'MMM dd, yyyy') : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body2">
                    ${currentExpense.reimbursementAmount ? currentExpense.reimbursementAmount.toFixed(2) : currentExpense.totalAmount ? currentExpense.totalAmount.toFixed(2) : '0.00'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Reference
                  </Typography>
                  <Typography variant="body2">
                    {currentExpense.reimbursementReference}
                  </Typography>
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Receipts Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card level={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Receipts
            </Typography>
            {currentExpense.status !== 'reimbursed' && (
              <Button
                startIcon={<UploadIcon />}
                onClick={() => setReceiptDialogOpen(true)}
                size="small"
              >
                Upload Receipt
              </Button>
            )}
          </Box>
          
          <SecureReceiptViewer
            receipts={currentExpense.receipts || []}
            onDelete={currentExpense.status !== 'reimbursed' ? handleReceiptDelete : null}
            showDelete={currentExpense.status !== 'reimbursed'}
          />
        </Card>
      </TabPanel>

      {/* History Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card level={1}>
          <Typography variant="h6" gutterBottom>
            Approval History
          </Typography>
          
          {!currentExpense.approvalHistory || currentExpense.approvalHistory.length === 0 ? (
            <Typography color="textSecondary" textAlign="center" py={4}>
              No approval history
            </Typography>
          ) : (
            <List>
              {(currentExpense.approvalHistory || []).map((history, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <TimelineIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            {history.action.charAt(0).toUpperCase() + history.action.slice(1)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            by {history.userId.firstName} {history.userId.lastName}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="textSecondary">
                            {history.date ? format(new Date(history.date), 'MMM dd, yyyy HH:mm') : 'N/A'}
                          </Typography>
                          {history.comment && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {history.comment}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Card>
      </TabPanel>

      {/* Action Dialog (Approve/Reject) */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve' : 'Reject'} Expense
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            fullWidth
            multiline
            rows={3}
            value={actionComment}
            onChange={(e) => setActionComment(e.target.value)}
            required={actionType === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleActionSubmit}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            disabled={actionType === 'reject' && !actionComment}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Upload Dialog */}
      <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)}>
        <DialogTitle>Upload Receipt</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Please select an image or PDF file
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleReceiptUpload} 
            variant="contained"
            disabled={!selectedFile}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog />
    </Box>
  );
};

export default ExpenseDetail;